import { useContext } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames/bind";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BadgeCore } from "app/components/ui/badge/BadgeCore";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { generateDestinationTitle, generateHotelName } from "app/utils/productUtils";
import { useProductLink } from "../hooks/useProductLink";
import { PrePackageCard } from "./PrePackageCard";
import styles from "./RecentlyViewedTrips.module.scss";

const cx = classNames.bind(styles);

export type RecentlyViewedTrip = {
	id: string;
	uri: string;
	name?: string;
	category?: number;
	categoryType?: string;
	country?: string;
	region?: string;
	resort?: string;
	/** Only the first photo is shown. `thumbnail` is a raw base64 JPEG. */
	photos?: { url: string; thumbnail?: string }[];
	/** Absolute url of the quote the user started on this trip. */
	quotationURL?: string;
	/** Time left before the end of the sale, computed from `endAt`. */
	expiration?: { day: number; hour: number };
};

export type RecentlyViewedTripCardProps = {
	trip: RecentlyViewedTrip;
	photoSizes: string;
};

/** A trip of the "recently viewed" section: links to the quote in progress, or to the product page. */
export const RecentlyViewedTripCard = ({ trip, photoSizes }: RecentlyViewedTripCardProps) => {
	const { formatMessage } = useIntl();
	const { shop, resolution } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();

	const isExpired = trip.expiration?.day === 0 && trip.expiration?.hour === 0;
	const hasQuote = Boolean(trip.quotationURL) && !isExpired;

	const productLink = useProductLink({ productUri: trip.uri });

	const title = generateDestinationTitle(shop, trip);
	const subtitle = generateHotelName(trip.name, trip.category);
	const [firstPhoto] = trip.photos ?? [];
	const photo = firstPhoto?.url
		? {
				url: firstPhoto.url,
				thumbnail: firstPhoto.thumbnail,
			}
		: undefined;

	const handleClick = () => {
		track("listingPage_navlog_click", {
			quotationURL: trip.quotationURL,
			productUri: trip.uri,
		});
		track("last_seen_product_click", {
			product: {
				uri: trip.uri,
				resolution,
				photo: { url: firstPhoto?.url },
				name: trip.name,
				expiration: trip.expiration,
				region: trip.region,
				resort: trip.resort,
				country: trip.country,
				category: trip.category,
				categoryType: trip.categoryType,
				quotationURL: trip.quotationURL,
				lastSeenProductType: trip.quotationURL ? "quote" : "product",
			},
		});
	};

	return (
		<PrePackageCard
			to={hasQuote && trip.quotationURL ? trip.quotationURL : productLink.to}
			onClick={handleClick}
			title={title}
			subtitle={subtitle}
			photos={photo ? [photo] : []}
			photosLabel={subtitle}
			photoSizes={photoSizes}
			badges={
				isExpired ? (
					<BadgeCore
						color="var(--color-gray-600)"
						primaryText={formatMessage({ id: "product.deal.expired.label" })}
					/>
				) : (
					hasQuote && <BadgeItem type="quote" />
				)
			}
			variant="ghost"
			data-expired={isExpired}
			data-testid="recently-viewed-trip"
			className={cx("recently-viewed-trips__trip")}
		/>
	);
};
